<script setup lang="ts">
import CoreIcon from '@core/core-icon.vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const platforms = [
	{ icon: 'apple', viewBox: '0 0 24 24', label: 'iOS' },
	{ icon: 'android', viewBox: '0 0 24 24', label: 'Android' },
	{ icon: 'macos', viewBox: '0 0 24 24', label: 'MacOS' },
	{ icon: 'windows', viewBox: '0 0 24 24', label: 'Windows' },
];
</script>

<template>
	<section class="section-welcome-promo">
		<div class="section-welcome-promo__inner">
			<!-- Hexagon decoration -->
			<img
				src="/images/welcome/hexagon-ring.png"
				alt=""
				class="section-welcome-promo__hexagon"
			/>

			<div class="section-welcome-promo__content">
				<h2 class="section-welcome-promo__title">{{ t('welcome.promo.title') }}</h2>

				<p class="section-welcome-promo__description">{{ t('welcome.promo.description') }}</p>

				<div class="section-welcome-promo__platforms">
					<div
						v-for="platform in platforms"
						:key="platform.icon"
						class="section-welcome-promo__platform"
					>
						<core-icon
							:name="platform.icon"
							:viewBox="platform.viewBox"
							class="section-welcome-promo__platform-icon"
						/>
						<span class="section-welcome-promo__platform-label">{{ platform.label }}</span>
					</div>
				</div>

				<div class="section-welcome-promo__badge">
					{{ t('welcome.promo.badge') }}
				</div>
			</div>

			<div class="section-welcome-promo__mockup">
				<img
					src="/images/welcome/iphone-mockup.png"
					alt=""
					class="section-welcome-promo__mockup-img"
				/>
			</div>
		</div>
	</section>
</template>

<style lang="scss">
.section-welcome-promo {
	margin-top: 4rem;
	padding: 0 1.5rem;

	@include min-desktop {
		margin-top: 6rem;
		padding: 0 8rem;
	}

	&__inner {
		max-width: 133.6rem;
		margin: 0 auto;
		background: linear-gradient(162deg, #735DCF 24%, rgba(115, 93, 207, 0) 90%),
					linear-gradient(90deg, rgba(115, 93, 207, 0.25) 0%, rgba(115, 93, 207, 0.25) 100%);
		border-radius: 3.2rem;
		padding: 2.4rem;
		overflow: hidden;
		display: flex;
		flex-direction: column;
		gap: 2rem;
    position: relative;

		@include min-desktop {
			border-radius: 6.4rem;
			padding: 6.4rem;
			gap: 3rem;
			min-height: 44rem;
		}
	}

	&__hexagon {
    position: absolute;
    width: 130%;
    max-width: 560px;
    height: auto;
    opacity: 0.55;
    pointer-events: none;
    bottom: -15rem;
    left: 50%;
    transform: translateX(-50%);


		@include min-desktop {
      width: 68rem;
      height: 68rem;
      right: 8rem;
      top: 12rem;
      left: auto;
      transform: none;
      max-width: none;
		}
	}

	&__content {
		display: flex;
		flex-direction: column;
		gap: 2rem;
		position: relative;
		z-index: 1;

		@include min-desktop {
			gap: 3rem;
			max-width: 50rem;
		}
	}

	&__badge {
		display: inline-flex;
		align-self: flex-start;
		padding: 1.2rem 2.4rem;
		border: 1px solid rgba(255, 255, 255, 0.24);
		border-radius: 10rem;
		color: #fff;
		@include font-regular;
		order: -1;

		@include min-desktop {
			@include font-18-pro-medium;
			height: 6.2rem;
			align-items: center;
			order: unset;
		}
	}

	&__title {
		color: #fff;
		font-size: 3.2rem;
		font-weight: 500;
		line-height: 1;

		@include min-desktop {
			font-size: 4.8rem;
			max-width: 47.2rem;
		}
	}

	&__description {
		color: #fff;
		@include font-18-pro-regular;
		line-height: 1.2;

    @include min-desktop {
      @include font-16-pro-regular;
    }
	}

	&__platforms {
		display: flex;
		flex-wrap: wrap;
		gap: 0.8rem;
    padding-bottom: 2rem;

		@include min-desktop {
			gap: 2.4rem;
      padding-bottom: 0;
		}
	}

	&__platform {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 0.4rem;
		width: calc(50% - 0.4rem);
		height: 7rem;
		background: rgba(255, 255, 255, 0.06);
		border: 1px solid rgba(115, 93, 207, 0.12);
		border-radius: 1.6rem;
		cursor: default;

		@include min-desktop {
			width: 10rem;
			height: 10rem;
			border-radius: 30rem;
		}
	}

	&__platform-icon {
		width: 3.6rem;
		height: 3.6rem;
		fill: #fff;
	}

	&__platform-label {
		@include font-regular;
		color: #fff;
	}

	&__mockup {
		display: flex;
		justify-content: center;
		position: relative;
		z-index: 1;
    top: 2.4rem;
    margin-top: 2rem;

		@include min-desktop {
			position: absolute;
      bottom: 0;
			right: 25rem;
      top: 6.4rem;
		}
	}

	&__mockup-img {
		max-width: 28rem;
		height: auto;
		object-fit: contain;

		@include min-desktop {
			width: 34rem;
			height: auto;
			max-width: none;
		}
	}
}
</style>
